/* ============================================================
   dispatch.js: when an order placed now is dispatched, and whether the
   office is open. The one place this is worked out: the product page line,
   the "Order online with confidence" card, the trust strip and the basket
   all use it (through assets/delivery.js), so they never disagree.

   - UK time (Europe/London) from the visitor's clock, whatever their own
     time zone, via Intl.DateTimeFormat. BST/GMT changes are handled by
     Intl; day arithmetic is done on calendar dates in UTC so a clock
     change can't shift a day.
   - Dispatch days: Monday to Friday, except the non-dispatch dates in
     Theme settings > Free delivery & dispatch (bank holidays, Christmas
     closure). Cutoff: the same setting's hour (14 = 2pm).
   - Office hours: 09:00 to 17:00 on dispatch days.
   - Pure functions taking a Date, so tests/dispatch.test.js can fake the
     clock. No network calls.
   ============================================================ */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.FAWPDispatch = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  var DAY_MS = 86400000;

  /* Non-dispatch dates from the theme setting: one date per line
     (YYYY-MM-DD), or a range "YYYY-MM-DD to YYYY-MM-DD". Anything else on
     a line (a note such as "Christmas Day") is ignored. */
  function parseClosed(text) {
    var out = {};
    String(text || '').split(/[\r\n,;]+/).forEach(function (line) {
      var d = line.match(/\d{4}-\d{2}-\d{2}/g);
      if (!d) return;
      if (d.length === 2 && /\d{4}-\d{2}-\d{2}\s*(to|–)\s*\d{4}-\d{2}-\d{2}/i.test(line)) {
        var a = Date.parse(d[0] + 'T00:00:00Z'), b = Date.parse(d[1] + 'T00:00:00Z');
        if (!isNaN(a) && !isNaN(b) && b >= a && (b - a) / DAY_MS <= 60) {
          for (var t = a; t <= b; t += DAY_MS) out[new Date(t).toISOString().slice(0, 10)] = true;
          return;
        }
      }
      d.forEach(function (iso) { out[iso] = true; });
    });
    return out;
  }

  /* The date and time in the UK at `date`. */
  function ukParts(date) {
    var parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
    }).formatToParts(date);
    var o = {};
    parts.forEach(function (p) { o[p.type] = p.value; });
    return { y: +o.year, m: +o.month, d: +o.day, h: (+o.hour) % 24, min: +o.minute };
  }

  /* The calendar day `offset` days after y-m-d. */
  function calendarDay(y, m, d, offset) {
    var t = new Date(Date.UTC(y, m - 1, d + offset));
    return { iso: t.toISOString().slice(0, 10), dow: t.getUTCDay(), date: t.getUTCDate(), month: t.getUTCMonth(), ahead: offset };
  }

  function isDispatchDay(day, closed) {
    return day.dow >= 1 && day.dow <= 5 && !closed[day.iso];
  }

  /* Everything the placements need, for the moment `date`.
     opts: cutoffHour (default 14), closed (from parseClosed), openHour
     (default 9), closeHour (default 17). */
  function state(date, opts) {
    opts = opts || {};
    var closed = opts.closed || {};
    var cutoff = parseInt(opts.cutoffHour, 10);
    if (isNaN(cutoff)) cutoff = 14;
    var openH = opts.openHour == null ? 9 : opts.openHour;
    var closeH = opts.closeHour == null ? 17 : opts.closeHour;

    var now = ukParts(date);
    var nowMin = now.h * 60 + now.min;
    var today = calendarDay(now.y, now.m, now.d, 0);
    var todayOpen = isDispatchDay(today, closed);
    var beforeCutoff = todayOpen && nowMin < cutoff * 60;

    var next = null, nextOpen = null;
    if (beforeCutoff) next = today;
    if (todayOpen && nowMin < openH * 60) nextOpen = today;
    for (var k = 1; k <= 45 && (!next || !nextOpen); k++) {
      var day = calendarDay(now.y, now.m, now.d, k);
      if (!isDispatchDay(day, closed)) continue;
      if (!next) next = day;
      if (!nextOpen) nextOpen = day;
    }

    return {
      now: now,
      today: today,
      isDispatchDay: todayOpen,
      beforeCutoff: beforeCutoff,
      minutesLeft: beforeCutoff ? cutoff * 60 - nowMin : 0,
      officeOpen: todayOpen && nowMin >= openH * 60 && nowMin < closeH * 60,
      next: next,           // next dispatch day (today while before the cutoff)
      nextOpen: nextOpen,   // the dispatch day the office next opens (at openHour)
      cutoffHour: cutoff,
      openHour: openH
    };
  }

  /* ---------- Wording ---------- */

  function plural(n, one, many) { return n + ' ' + (n === 1 ? one : many); }

  /* "2 hrs 14 mins", "1 hr", "45 mins", "1 min". */
  function countdown(mins) {
    var h = Math.floor(mins / 60), m = mins % 60, out = [];
    if (h) out.push(plural(h, 'hr', 'hrs'));
    if (m || !h) out.push(plural(m, 'min', 'mins'));
    return out.join(' ');
  }

  /* "Thursday 22 October". */
  function dayLabel(day) { return DAY_NAMES[day.dow] + ' ' + day.date + ' ' + MONTH_NAMES[day.month]; }

  /* When something happens on `day`, as a phrase after a verb:
     "today", "tomorrow, Thursday 22 October", "on Monday 26 October". */
  function whenPhrase(day) {
    if (!day) return 'on the next working day';
    if (day.ahead === 0) return 'today';
    if (day.ahead === 1) return 'tomorrow, ' + dayLabel(day);
    return 'on ' + dayLabel(day);
  }

  /* Short form for tight spaces: "tomorrow", "Monday", "Monday 4 January". */
  function whenShort(day) {
    if (!day) return 'next working day';
    if (day.ahead === 0) return 'today';
    if (day.ahead === 1) return 'tomorrow';
    if (day.ahead <= 6) return DAY_NAMES[day.dow];
    return dayLabel(day);
  }

  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  /* All the wording, from one state. HTML strings where a part is bold. */
  function messages(st, opts) {
    opts = opts || {};
    var cutLabel = st.cutoffHour > 12 ? (st.cutoffHour - 12) + 'pm' : st.cutoffHour === 12 ? '12pm' : st.cutoffHour + 'am';
    var openLabel = st.openHour > 12 ? (st.openHour - 12) + 'pm' : st.openHour + 'am';
    var m = {};

    /* 1. Product page, under the price. */
    m.product = st.beforeCutoff
      ? 'Order within <strong>' + countdown(st.minutesLeft) + '</strong> for dispatch today.'
      : 'Order now for dispatch ' + whenPhrase(st.next) + '.';

    /* 2. "Order online with confidence" card, out of hours. */
    m.helpDispatch = 'In-stock items ordered now are dispatched ' + whenPhrase(st.next) + '.';
    var reply = st.nextOpen && st.nextOpen.ahead === 0 ? 'today'
      : st.nextOpen && st.nextOpen.ahead === 1 ? 'tomorrow'
      : st.nextOpen ? 'on ' + dayLabel(st.nextOpen) : 'on the next working day';
    m.helpReply = 'from ' + openLabel + ' ' + reply;

    /* 3. Trust strip column. Before the cutoff the Liquid wording stays. */
    if (st.beforeCutoff) {
      m.usp = 'Order by ' + cutLabel + ', dispatched same day';
    } else {
      var lead = st.now.h >= 17 ? 'Order tonight'
        : (st.today.dow === 0 || st.today.dow === 6) ? 'Order this weekend'
        : 'Order now';
      m.usp = lead + ', dispatched ' + whenShort(st.next);
    }

    /* 4. Basket, above Checkout. */
    m.cart = st.beforeCutoff
      ? 'In-stock items dispatched today if you order within <strong>' + countdown(st.minutesLeft) + '</strong>.'
      : 'Order now, dispatched ' + whenPhrase(st.next) + '.';
    m.cartMixed = 'Dispatch dates for each item are confirmed by email after you order.';

    m.officeOpen = st.officeOpen;
    m.esc = esc;
    return m;
  }

  return {
    parseClosed: parseClosed,
    ukParts: ukParts,
    state: state,
    messages: messages,
    countdown: countdown,
    dayLabel: dayLabel,
    whenPhrase: whenPhrase,
    whenShort: whenShort
  };
});
